import { useRef, useState, type FormEvent } from "react";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { userAction } from "@/core/telemetry/userAction";
import { Button } from "@/shared/components/shadcn/Button";
import { Checkbox } from "@/shared/components/shadcn/Checkbox";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useSendFeedback } from "../hooks/feedbackQueries";
import { FEEDBACK_MESSAGE_MAX_LENGTH, SendFeedbackRequestSchema } from "../types/feedbackTypes";

export function FeedbackPage() {
  const { user } = useAuth();
  const navigation = useAppNavigation();
  const send = useSendFeedback();
  const [message, setMessage] = useState("");
  const [allowContact, setAllowContact] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [sent, setSent] = useState(false);
  const messageInput = useRef<HTMLTextAreaElement>(null);
  const email = user?.email;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (send.isPending) return;
    const parsed = SendFeedbackRequestSchema.safeParse({ message, allowContact: allowContact && !!email });
    if (!parsed.success) {
      setMessageError(parsed.error.issues[0].message);
      messageInput.current?.focus();
      return;
    }
    send.mutate(parsed.data, { onSuccess: () => setSent(true) });
  }

  if (sent) {
    return (
      <Page>
        <PageHeader title="Feedback" />
        <PageContent className="max-w-[660px]">
          <EmptyState
            title="Thanks for your feedback"
            message={allowContact && email ? "We read every message and may reply by email." : "We read every message."}
            action={{ label: "Back to More", variant: "outline", onClick: navigation.goToApps }}
          />
        </PageContent>
      </Page>
    );
  }

  return (
    <FormPage
      header={<PageHeader title="Feedback" />}
      onSubmit={submit}
      contentClassName="mx-auto w-full max-w-[660px]"
      actions={<Button type="submit" {...userAction("feedback.form_submit")} size="touch" disabled={send.isPending}>{send.isPending ? "Sending..." : "Send feedback"}</Button>}
    >
      <FormField id="feedback-message" label="Your feedback" required error={messageError} description="Share an idea, report a problem or tell us what you think.">
        <TextArea
          ref={messageInput} name="message" value={message} rows={8} maxLength={FEEDBACK_MESSAGE_MAX_LENGTH}
          data-telemetry-mask disabled={send.isPending}
          onChange={(event) => { setMessageError(""); send.reset(); setMessage(event.target.value); }}
        />
      </FormField>
      <p className="-mt-5 text-right text-xs text-muted-foreground">{message.length}/{FEEDBACK_MESSAGE_MAX_LENGTH}</p>
      {email && (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={allowContact} disabled={send.isPending} onCheckedChange={(value) => setAllowContact(value === true)} />
          <span className="pt-3">You can contact me at <strong className="break-all" data-telemetry-mask>{email}</strong></span>
        </label>
      )}
      {send.error && <ErrorState title="Couldn't send feedback" message={`${send.error.message} Your message has been kept.`} />}
    </FormPage>
  );
}
